/**
 * Page Analytics — utilisateurs internes uniquement
 * Vue globale de tous les projets avec filtre par projet
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import backendApi from '../../services/backendApi';
import { ChevronDown, CircleDot, Layers3 } from 'lucide-react';
import { computeTicketsByClient, buildColorMap } from '../../utils/dashboardMetrics';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

import TicketsByProjectPie from './TicketsByProjectPie';
import MilestonesByTicketsChart from './MilestonesByTicketsChart';
import ProjectHealthGauge from './ProjectHealthGauge';
import SpPipelineChart from './SpPipelineChart';
import DevPerformanceChart from './DevPerformanceChart';
import UsToEstimateChart from './UsToEstimateChart';
import BacklogByProjectChart from './BacklogByProjectChart';
import UsToTestChart from './UsToTestChart';
import RaffinageByProjectChart from './RaffinageByProjectChart';

const ProjectFilter = ({ projects, selectedId, onChange }) => (
  <div className="relative">
    <select
      value={selectedId ?? ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
      aria-label="Filtrer par projet"
      className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10
                 text-sm font-medium text-slate-700 shadow-sm cursor-pointer sm:w-auto
                 focus:outline-none focus:ring-2 focus:ring-[#8768B5] focus:border-[#8768B5]
                 hover:border-slate-300 transition-colors"
    >
      <option value="">Tous les projets</option>
      {projects.map(p => (
        <option key={p.id} value={p.id}>
          {p.name}{p.partnerName ? ` — ${p.partnerName}` : ''}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
  </div>
);

const AnalyticsPage = () => {
  const { isInternalUser } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [devData, setDevData] = useState({ devPerformance: [], devNames: [], dailyData: [] });
  const [devLoading, setDevLoading] = useState(false);
  // Conserve la dernière bonne réponse pour éviter d'écraser les données affichées
  // lors d'une erreur transitoire Odoo (projects vide, 503, etc.)
  const lastGoodDataRef = useRef(null);

  useEffect(() => {
    if (!isInternalUser) navigate('/dashboard/tickets', { replace: true });
  }, [isInternalUser, navigate]);

  const fetchDevPerformance = useCallback((projectId) => {
    if (!isInternalUser) return;
    setDevLoading(true);
    backendApi.getDevPerformance(false, projectId)
      .then(dev => setDevData({
        devPerformance: dev.devPerformance || [],
        devNames: dev.devNames || [],
        dailyData: dev.dailyData || []
      }))
      .catch(() => {})
      .finally(() => setDevLoading(false));
  }, [isInternalUser]);

  const fetchAll = useCallback(() => {
    if (!isInternalUser) return;
    setLoading(true);
    setError(null);
    Promise.all([
      backendApi.getAnalyticsOverview(false),
      backendApi.getDevPerformance(false, null)
    ]).then(([overview, dev]) => {
      // Guard : si Odoo renvoie des projets vides (erreur transitoire), garder les données précédentes
      if ((overview.projects?.length ?? 0) > 0) {
        lastGoodDataRef.current = overview;
        setData(overview);
        setError(null);
      } else if (lastGoodDataRef.current) {
        console.warn('⚠️ API returned empty projects — keeping last good data');
      } else {
        setData(overview);
      }
      setDevData({
        devPerformance: dev.devPerformance || [],
        devNames: dev.devNames || [],
        dailyData: dev.dailyData || []
      });
    }).catch(err => {
      // En cas d'erreur (ex: 503 Odoo indispo), si on a déjà des données, on les conserve
      if (lastGoodDataRef.current) {
        console.warn('⚠️ Fetch error but keeping last good data:', err.message);
      } else {
        setError(err.message);
      }
    }).finally(() => setLoading(false));
  }, [isInternalUser]);

  useAutoRefresh(fetchAll, 5 * 60 * 1000);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Re-fetch dev performance quand le projet sélectionné change
  useEffect(() => {
    fetchDevPerformance(selectedProjectId);
  }, [selectedProjectId, fetchDevPerformance]);

  const filtered = useMemo(() => {
    if (!data) return { usStageGroups: [], usRaffinageGroups: [], tickets: [], milestones: [], ticketsByProject: [], doneTasksCount: null, totalTasksCount: null, resolvedTicketsCount: null, botResolvedTicketsCount: 0, totalTicketsCount: null };
    const { usStageGroups, usRaffinageGroups = [], tickets, milestones, ticketsByProject, doneTasksCount = null, totalTasksCount = null, resolvedTicketsCount = null, botResolvedTicketsCount = 0, totalTicketsCount = null } = data;
    if (!selectedProjectId) return { usStageGroups, usRaffinageGroups, tickets, milestones, ticketsByProject, doneTasksCount, totalTasksCount, resolvedTicketsCount, botResolvedTicketsCount, totalTicketsCount };
    const projectTickets = tickets.filter(t => t.projectId === selectedProjectId);
    return {
      usStageGroups: usStageGroups.filter(g => g.projectId === selectedProjectId),
      usRaffinageGroups: usRaffinageGroups.filter(g => g.projectId === selectedProjectId),
      tickets: projectTickets,
      milestones: milestones.filter(m => m.projectId === selectedProjectId),
      ticketsByProject: ticketsByProject.filter(t => t.projectId === selectedProjectId),
      doneTasksCount: null,
      totalTasksCount: null,
      resolvedTicketsCount: null,
      botResolvedTicketsCount: projectTickets.filter(ticket => ticket.botOutcome === 'resolved').length,
      totalTicketsCount: null
    };
  }, [data, selectedProjectId]);

  const clientColorMap = useMemo(() => {
    const allProjects = data?.projects || [];
    const clientNames = [
      ...allProjects.map(p => p.partnerName || p.name),
      ...(filtered.tickets || []).map(t => t.clientName)
    ];
    return buildColorMap(clientNames);
  }, [data, filtered.tickets]);

  const projectColorMap = useMemo(() => {
    const allProjects = data?.projects || [];
    const map = {};
    allProjects.forEach(p => {
      map[p.id] = clientColorMap[p.partnerName || p.name] || '#6B7280';
    });
    return map;
  }, [data, clientColorMap]);

  if (!isInternalUser) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-8 pt-8">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex flex-col items-center gap-4 text-center">
          <p className="text-red-700 text-sm">Erreur : {error}</p>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const { usStageGroups, usRaffinageGroups, tickets, milestones, doneTasksCount, totalTasksCount, resolvedTicketsCount, botResolvedTicketsCount, totalTicketsCount } = filtered;
  const projects = (data?.projects || []).slice().sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  const ticketsByClient = computeTicketsByClient(tickets);

  return (
    <div className="mx-auto max-w-[1720px] space-y-5 px-4 pb-8 pt-5 sm:px-6 lg:px-8 lg:pt-7">
      {/* Header */}
      <section className="relative overflow-hidden rounded-[24px] bg-[#382443] px-5 py-6 text-white shadow-[0_18px_50px_rgba(56,36,67,0.16)] sm:px-7">
        <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full border-[42px] border-white/[0.035]" />
        <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-1.5 text-xs text-white/70">
              <CircleDot className="h-3.5 w-3.5 text-[#F28AAF]" />
              Données synchronisées avec Odoo
            </div>
            <h1 className="text-2xl font-semibold tracking-[-0.025em] sm:text-3xl">Vue opérationnelle</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/60">
              {selectedProjectId
              ? `Projet : ${projects.find(p => p.id === selectedProjectId)?.name}`
              : `${projects.length} projets en cours · backlog, support et livraison réunis`}
          </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="hidden items-center gap-2 text-xs text-white/45 xl:flex">
              <Layers3 className="h-4 w-4" />
              Portefeuille actif
            </div>
            <ProjectFilter projects={projects} selectedId={selectedProjectId} onChange={setSelectedProjectId} />
          </div>
        </div>
      </section>

      {/* Row 1 : Santé + Tickets par client + Pipeline SP */}
      <div data-tour="analytics-health" className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_2fr_1fr]">
        <ProjectHealthGauge usStageGroups={usStageGroups} milestones={data?.milestones || []} tickets={tickets} doneTasksCount={doneTasksCount} totalTasksCount={totalTasksCount} resolvedTicketsCount={resolvedTicketsCount} botResolvedTicketsCount={botResolvedTicketsCount} totalTicketsCount={totalTicketsCount} />
        <TicketsByProjectPie data={ticketsByClient} colorMap={clientColorMap} />
        <SpPipelineChart usStageGroups={usStageGroups} />
      </div>

      {/* Row 2 : 4 trackers US */}
      <div data-tour="analytics-work" className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-4">
        <RaffinageByProjectChart usStageGroups={usRaffinageGroups} colorMap={projectColorMap} />
        <UsToEstimateChart usStageGroups={usStageGroups} colorMap={projectColorMap} />
        <BacklogByProjectChart usStageGroups={usStageGroups} colorMap={projectColorMap} />
        <UsToTestChart usStageGroups={usStageGroups} colorMap={projectColorMap} />
      </div>

      {/* Row 4 : Jalons */}
      <MilestonesByTicketsChart milestones={milestones} />

      {/* Row 5 : Performances développeurs */}
      {devLoading ? (
        <div className="flex items-center justify-center h-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <DevPerformanceChart
          devPerformance={devData.devPerformance}
          devNames={devData.devNames}
          dailyData={devData.dailyData}
          sprintActif={false}
        />
      )}
    </div>
  );
};

export default AnalyticsPage;
